import { computed, effectScope, ref, watch, type EffectScope } from 'vue'
import { Cartesian3, Cartographic } from 'cesium'
import { CesiumService } from '@/handlers/cesium.service'
import { useMapFunctions } from '../useMapFunctions'
import { appStore } from '@/store/app'
import { storeToRefs } from 'pinia'
import { useEntityLabels } from '../useEntityLabel'
import useUserSettings from '@/composables/useUserSettings'
import type { MapEntityPreference } from '@/types/zones.types'
import log from '@/utils/useLogger'

// todo: remove all entities from the viewer and use ids instead of entities

/**
 * One row of the map entities panel. `entity` is the row's representative
 * entity (used for "fly to"); `entities` lists every Cesium entity / HTML
 * label id the row's visibility toggle owns, when that is more than one.
 */
export interface MapPanelAsset {
  name: string
  icon: string
  visible: boolean | undefined
  entity: string | null
  entities?: (string | null)[]
  disabled: boolean
}

// Limit types to ensure consistency
export interface MusterPointEntity {
  labelEntity: string | null
  circleEntity: string | null
  id: string // Unique identifier for each muster point
}

// Keep track of entities in a single location
const droneEntity = ref<string | null>(null)
const musterLabels = useEntityLabels()
const portalEntities = ref<string[]>([])

// Geofence entities
const outerGeofenceEntity = ref<string | null>(null)
const innerGeofenceEntity = ref<string | null>(null)

// Safe location entities
const safeLocationBillboard = ref<string | null>(null)
const blueCircleEntity = ref<string | null>(null)
const yellowCircleEntity = ref<string | null>(null)

// Alternate safe location entities - only one alternate safe location allowed
const alternateSafeLocationBillboard = ref<string | null>(null)
const alternateBlueCircleEntity = ref<string | null>(null)
const alternateYellowCircleEntity = ref<string | null>(null)

// Home marker entities
const homeMarkerBillboard = ref<string | null>(null)
const homeBlueCircleEntity = ref<string | null>(null)

const takeoffHeightElevationEntity = ref<string | null>(null)
const takeoffHeightElevationLabelEntity = ref<string | null>(null)

// Muster point entities - limited to max 3
const musterPointEntities = ref<MusterPointEntity[]>([])
const connectingLine = ref<string | null>(null)
const altitudeLine = ref<string | null>(null)
const trailingPathEntity = ref<string | null>(null)
const gtlMarkerEntities = ref<string[]>([])
// Track the existing muster point IDs to prevent duplicates
const existingMusterPointIDs = new Set<string>()

/** Observability: which device group the asset manager is currently tracking. */
const currentGroupId = ref<string | null>(null)

/**
 * Detached effect scope owning the singleton watcher that re-applies persisted
 * entity-visibility preferences after entities are re-created. Detached (and
 * module-level) so it survives the unmount of whichever component happened to
 * call `useAssetManager()` first.
 */
let visibilityScope: EffectScope | null = null

/**
 * Group-keyed terrain cache overlay. Sits above the generic LRU
 * `terrainHeightCache` in `useMapFunctions` so swap-back is zero-cost even if
 * the LRU has evicted the key.
 */
type TerrainCacheKey = 'dockAmsl' | 'geofenceTerrainHeight'
const terrainCache = new Map<string, Partial<Record<TerrainCacheKey, number>>>()

/**
 * Plot counter: tracks nested `beginPlot()` / `endPlot()` calls across the
 * async-add pipeline. `isPlottingEntities` flips false only when every begun
 * plot op has ended, which guarantees the overlay cannot get stuck true if
 * one path forgets its finally-block (it still does, but this is a defensive
 * invariant).
 */
let plotCounter = 0
let lastPlotTouch = 0

export const useAssetManager = () => {
  const viewer = computed(() => CesiumService.viewer.value)
  const { removeEntityIfExists, getTerrainHeight } = useMapFunctions()
  const appInstance = appStore()
  const { isDjiDock3, slinkTechConfigurations, isPlottingEntities } = storeToRefs(appInstance)
  // User settings — persists which map entities the user hid so the choice
  // survives device-group swaps and reloads. Mirrors `dockPreferences`.
  const { settings, saveSettings } = useUserSettings()

  /** Persisted per-entity visibility preferences, keyed by panel name. */
  const entityVisibilityPrefs = computed<MapEntityPreference>(
    () => (settings.value?.mapEntityPreferences ?? {}) as MapEntityPreference,
  )

  /**
   * Increment the plot counter and flip the overlay on. Every call MUST be
   * paired with an `endPlot()` in a finally-block by the caller.
   */
  const beginPlot = () => {
    plotCounter++
    lastPlotTouch = Date.now()
    isPlottingEntities.value = true
  }

  /** Decrement the plot counter; flip the overlay off only when it hits zero. */
  const endPlot = () => {
    plotCounter = Math.max(0, plotCounter - 1)
    lastPlotTouch = Date.now()
    if (plotCounter === 0) isPlottingEntities.value = false
  }

  /**
   * Nuclear reset of the plot counter + overlay. Called by `cleanup()` paths
   * and the MapLayout watchdog. Use sparingly; prefer paired begin/end.
   */
  const forceResetPlot = () => {
    plotCounter = 0
    lastPlotTouch = Date.now()
    isPlottingEntities.value = false
  }

  /**
   * Has the overlay been true for too long without a begin/end touch? Used by
   * the MapLayout watchdog as a last-resort unstick.
   */
  const isPlotStuck = (thresholdMs: number) =>
    isPlottingEntities.value && plotCounter > 0 && Date.now() - lastPlotTouch > thresholdMs

  /** Group-keyed terrain cache helpers. Returns undefined if not cached. */
  const getCachedTerrain = (groupId: string | null, key: TerrainCacheKey): number | undefined => {
    if (!groupId) return undefined
    return terrainCache.get(groupId)?.[key]
  }
  const setCachedTerrain = (groupId: string | null, key: TerrainCacheKey, value: number) => {
    if (!groupId) return
    const bucket = terrainCache.get(groupId) ?? {}
    bucket[key] = value
    terrainCache.set(groupId, bucket)
  }
  const clearTerrainCache = (groupId?: string) => {
    if (groupId) terrainCache.delete(groupId)
    else terrainCache.clear()
  }

  /**
   * Remove every tracked Cesium entity and null the corresponding module-level
   * refs. Identity of the refs is preserved (only `.value` is mutated) so the
   * `assetsStore` computed binding consumed by `AppPanelLists.vue` stays
   * reactive without re-subscription. Muster HTML labels are also cleaned up.
   *
   * Does NOT clear `terrainCache` — swap-back uses it for zero-cost re-init.
   */
  const $reset = () => {
    try {
      // Geofence
      removeEntityIfExists(outerGeofenceEntity.value)
      removeEntityIfExists(innerGeofenceEntity.value)
      outerGeofenceEntity.value = null
      innerGeofenceEntity.value = null

      // Safe location
      removeEntityIfExists(safeLocationBillboard.value)
      removeEntityIfExists(blueCircleEntity.value)
      removeEntityIfExists(yellowCircleEntity.value)
      safeLocationBillboard.value = null
      blueCircleEntity.value = null
      yellowCircleEntity.value = null

      // Alternate safe location
      removeEntityIfExists(alternateSafeLocationBillboard.value)
      removeEntityIfExists(alternateBlueCircleEntity.value)
      removeEntityIfExists(alternateYellowCircleEntity.value)
      alternateSafeLocationBillboard.value = null
      alternateBlueCircleEntity.value = null
      alternateYellowCircleEntity.value = null

      // Home / dock
      removeEntityIfExists(homeMarkerBillboard.value)
      removeEntityIfExists(homeBlueCircleEntity.value)
      homeMarkerBillboard.value = null
      homeBlueCircleEntity.value = null

      // Takeoff height
      removeEntityIfExists(takeoffHeightElevationEntity.value)
      removeEntityIfExists(takeoffHeightElevationLabelEntity.value)
      takeoffHeightElevationEntity.value = null
      takeoffHeightElevationLabelEntity.value = null

      // Muster points (entities + ID set)
      musterPointEntities.value.forEach((entity) => {
        if (entity.labelEntity) {
          // HTML label managed by `musterLabels` — removed via cleanup() below
        }
        if (entity.circleEntity) removeEntityIfExists(entity.circleEntity)
      })
      musterPointEntities.value = []
      existingMusterPointIDs.clear()

      // Muster HTML labels
      musterLabels.cleanup()

      // Drone-entity-adjacent (owned by useDroneEntity but tracked here)
      removeEntityIfExists(droneEntity.value)
      removeEntityIfExists(connectingLine.value)
      removeEntityIfExists(altitudeLine.value)
      removeEntityIfExists(trailingPathEntity.value)
      droneEntity.value = null
      connectingLine.value = null
      altitudeLine.value = null
      trailingPathEntity.value = null

      // GTL markers
      gtlMarkerEntities.value.forEach((id) => removeEntityIfExists(id))
      gtlMarkerEntities.value = []

      // Portal entities
      portalEntities.value.forEach((id) => removeEntityIfExists(id))
      portalEntities.value = []
    } catch (err) {
      log.error('useAssetManager.$reset error', err)
    }
  }

  // Helper to add a new muster point in a controlled way
  const addMusterPointEntity = (musterEntity: MusterPointEntity) => {
    // Check if we already have this muster point by ID
    if (existingMusterPointIDs.has(musterEntity.id)) {
      // If we already have this muster point, update it instead of adding a new one
      const index = musterPointEntities.value.findIndex((entity) => entity.id === musterEntity.id)
      if (index >= 0) {
        // Remove old entities from the viewer
        const oldEntity = musterPointEntities.value[index]
        if (oldEntity.labelEntity) removeEntityIfExists(oldEntity.labelEntity)
        if (oldEntity.circleEntity) removeEntityIfExists(oldEntity.circleEntity)

        // Replace with new entities
        musterPointEntities.value[index] = musterEntity
      }
      return
    }

    // Enforce maximum of 3 muster points
    if (musterPointEntities.value.length >= 3) {
      // Remove the oldest muster point if we're at the limit
      const oldestEntity = musterPointEntities.value.shift()
      if (oldestEntity) {
        // Remove entities from the viewer
        if (oldestEntity.labelEntity) removeEntityIfExists(oldestEntity.labelEntity)
        if (oldestEntity.circleEntity) removeEntityIfExists(oldestEntity.circleEntity)
        // Remove from tracking set
        existingMusterPointIDs.delete(oldestEntity.id)
      }
    }

    // Add the new muster point
    musterPointEntities.value.push(musterEntity)
    existingMusterPointIDs.add(musterEntity.id)
  }

  // Helper to clear all muster points
  const clearAllMusterPoints = () => {
    if (!viewer.value) return

    // Remove all entities from the viewer
    musterPointEntities.value.forEach((entity) => {
      if (entity.labelEntity) removeEntityIfExists(entity.labelEntity)
      if (entity.circleEntity) removeEntityIfExists(entity.circleEntity)
    })

    // Clear arrays and tracking
    musterPointEntities.value = []
    existingMusterPointIDs.clear()
  }

  const assetsStore = computed<MapPanelAsset[]>(() => {
    let store: MapPanelAsset[] = [
      {
        name: 'Drone',
        icon: 'location_pin',
        visible: droneEntity.value
          ? viewer.value?.entities.getById(droneEntity.value)?.show
          : false,
        entity: droneEntity.value,
        disabled: !droneEntity.value,
      },
      {
        name: 'Dock',
        icon: 'location_pin',
        visible: homeMarkerBillboard.value
          ? viewer.value?.entities.getById(homeMarkerBillboard.value)?.show
          : false,
        entity: homeMarkerBillboard.value,
        entities: [homeBlueCircleEntity.value, homeMarkerBillboard.value],
        disabled: !homeMarkerBillboard.value,
      },
      {
        name: 'Safe Location',
        icon: 'location_pin',
        visible: safeLocationBillboard.value
          ? viewer.value?.entities.getById(safeLocationBillboard.value)?.show
          : false,
        entity: safeLocationBillboard.value,
        entities: [blueCircleEntity.value, safeLocationBillboard.value, yellowCircleEntity.value],
        disabled: !safeLocationBillboard.value,
      },
    ]

    // Only add alternate safe location if it exists
    if (alternateSafeLocationBillboard.value) {
      store.push({
        name: 'Alternate Safe Location',
        icon: 'location_pin',
        visible: alternateSafeLocationBillboard.value
          ? viewer.value?.entities.getById(alternateSafeLocationBillboard.value)?.show
          : false,
        entity: alternateSafeLocationBillboard.value,
        entities: [
          alternateBlueCircleEntity.value,
          alternateSafeLocationBillboard.value,
          alternateYellowCircleEntity.value,
        ],
        disabled: !alternateSafeLocationBillboard.value,
      })
    }

    // Add muster points (max 3)
    if (musterPointEntities.value.length > 0) {
      musterPointEntities.value.forEach((musterPoint, index) => {
        store.push({
          name: `M ${index + 1}`,
          icon: 'location_pin',
          visible: musterPoint.circleEntity
            ? viewer.value?.entities.getById(musterPoint.circleEntity)?.show
            : false,
          entity: musterPoint.circleEntity,
          entities: [musterPoint.circleEntity, musterPoint.labelEntity],
          disabled: !musterPoint.circleEntity,
        })
      })
    }

    if (outerGeofenceEntity.value && innerGeofenceEntity.value) {
      store.push({
        name: 'Geofence',
        icon: 'location_pin',
        visible: outerGeofenceEntity.value
          ? viewer.value?.entities.getById(outerGeofenceEntity.value)?.show
          : false,
        entity: outerGeofenceEntity.value,
        entities: [outerGeofenceEntity.value, innerGeofenceEntity.value],
        disabled: !outerGeofenceEntity.value,
      })
    }

    if (connectingLine.value) {
      store.push({
        name: 'Connecting Line',
        icon: 'location_pin',
        visible: connectingLine.value
          ? viewer.value?.entities.getById(connectingLine.value)?.show
          : false,
        entity: connectingLine.value,
        disabled: !connectingLine.value,
      })
    }

    if (altitudeLine.value) {
      store.push({
        name: 'Altitude Line',
        icon: 'location_pin',
        visible: altitudeLine.value
          ? viewer.value?.entities.getById(altitudeLine.value)?.show
          : false,
        entity: altitudeLine.value,
        disabled: !altitudeLine.value,
      })
    }

    if (trailingPathEntity.value) {
      store.push({
        name: 'Flight Path',
        icon: 'location_pin',
        visible: trailingPathEntity.value
          ? viewer.value?.entities.getById(trailingPathEntity.value)?.show
          : false,
        entity: trailingPathEntity.value,
        disabled: !trailingPathEntity.value,
      })
    }

    if (takeoffHeightElevationEntity.value) {
      store.push({
        name: 'Takeoff Height Elevation',
        icon: 'location_pin',
        visible: takeoffHeightElevationEntity.value
          ? viewer.value?.entities.getById(takeoffHeightElevationEntity.value)?.show
          : false,
        entity: takeoffHeightElevationEntity.value,
        entities: [takeoffHeightElevationEntity.value, takeoffHeightElevationLabelEntity.value],
        disabled: !takeoffHeightElevationEntity.value,
      })
    }

    // Add SlinkTech portal if it exists
    if (portalEntities.value.length > 0) {
      store.push({
        name: 'SlinkTech Portal',
        icon: 'location_pin',
        visible: portalEntities.value[0]
          ? viewer.value?.entities.getById(portalEntities.value[0])?.show
          : false,
        entity: portalEntities.value[0],
        entities: portalEntities.value,
        disabled: !portalEntities.value[0],
      })
    }

    return store
  })

  const goToEntity = async (id: string | null, name: string) => {
    if (viewer.value && id) {
      // Check if viewer and entity exist
      let position

      if (name === 'SlinkTech Portal') {
        if (slinkTechConfigurations.value?.longitude && slinkTechConfigurations.value?.latitude) {
          const lng = Number(slinkTechConfigurations.value.longitude)
          const lat = Number(slinkTechConfigurations.value.latitude)
          const terrainHeight = await getTerrainHeight(lng, lat)
          position = Cartesian3.fromDegrees(lng, lat, terrainHeight)
        }
      } else if (name === 'Geofence' || name === 'Connecting Line' || name === 'Altitude Line') {
        // const res = name === 'Geofence' ? safeLocationBillboard.value : droneEntity.value
        let res = droneEntity.value
        if (name === 'Geofence') {
          res = isDjiDock3.value ? homeMarkerBillboard.value : safeLocationBillboard.value
        }
        const entity = viewer.value?.entities.getById(res as string)
        position = entity?.position?.getValue()
      } else {
        const entity = viewer.value?.entities.getById(id)
        if (!entity) return
        position = entity.position?.getValue()
      }

      if (position) {
        // Convert position to cartographic (longitude, latitude, height)
        const cartographic = Cartographic.fromCartesian(position)
        // Add 500 meters altitude offset to view from above
        cartographic.height += 600
        // Convert back to Cartesian3
        const destinationWithOffset = Cartesian3.fromRadians(
          cartographic.longitude,
          cartographic.latitude,
          cartographic.height,
        )

        viewer.value.camera.flyTo({
          destination: destinationWithOffset,
          duration: 1,
        })
      }
    }
  }

  /**
   * Drive every Cesium entity (and muster HTML label) belonging to one panel
   * row to the same visibility. Setting them all explicitly — instead of
   * flipping each one's own `show` — keeps the group in step when they start
   * out desynced (e.g. a re-plotted circle next to a still-hidden label).
   */
  const setAssetVisibility = (asset: MapPanelAsset, visible: boolean) => {
    const ids: (string | null)[] = asset.entities ?? (asset.entity ? [asset.entity] : [])
    ids.forEach((id) => {
      if (!id) return
      if (id.startsWith('muster-point-label-')) {
        musterLabels.toggle(id, visible)
      } else {
        const entity = viewer.value?.entities.getById(id)
        if (entity && entity.show !== visible) entity.show = visible
      }
    })
  }

  /**
   * Persist one panel row's visibility. Keyed by name, so the preference
   * follows the user across device groups (FLY3D-1034). Throttled cloud sync
   * is handled inside `saveSettings`.
   */
  const persistEntityVisibility = (name: string, visible: boolean) => {
    const prefs = entityVisibilityPrefs.value
    if (prefs[name]?.visible === visible) return
    saveSettings({
      mapEntityPreferences: {
        ...prefs,
        [name]: { ...(prefs[name] ?? {}), visible },
      },
    }).catch((err) => log.error('[useAssetManager] persistEntityVisibility error', err))
  }

  /**
   * Push the stored preferences back onto the entities currently on the map.
   * Needed because `$reset()` destroys every tracked entity on a device-group
   * swap and the re-plot recreates them at their creation-time `show` — the
   * preference cannot be read off the entity, it has to be re-applied.
   */
  const applyPersistedVisibility = () => {
    if (!viewer.value) return
    const prefs = entityVisibilityPrefs.value
    assetsStore.value.forEach((asset) => {
      if (asset.disabled) return
      const pref = prefs[asset.name]
      if (!pref) return
      setAssetVisibility(asset, pref.visible)
    })
  }

  /**
   * Primitive snapshot of the panel's visibility state. Kept as a string so
   * the watcher below bails out on unrelated `assetsStore` invalidations (the
   * computed reads through the deeply-reactive Cesium viewer, so it recomputes
   * whenever the entity collection is touched).
   */
  const visibilitySignature = computed(() =>
    assetsStore.value.map((a) => `${a.name}:${a.entity ?? '-'}:${a.visible ? 1 : 0}`).join('|'),
  )

  /**
   * Install the singleton re-apply watcher in a detached scope. Fires when the
   * plotted entities change (device-group swap, re-plot) and when the settings
   * themselves load or sync in from another device.
   */
  const ensurePersistedVisibilityWatcher = () => {
    if (visibilityScope) return
    visibilityScope = effectScope(true)
    visibilityScope.run(() => {
      watch([visibilitySignature, entityVisibilityPrefs], () => applyPersistedVisibility(), {
        flush: 'post',
      })
    })
  }

  const toggleEntityVisibility = (name: string) => {
    const asset = assetsStore.value.find((asset) => asset.name === name)
    if (!asset) return
    const next = !(asset.visible ?? false)
    setAssetVisibility(asset, next)
    persistEntityVisibility(name, next)
  }

  // Helper to ensure we only have one alternate safe location
  const setAlternateSafeLocation = (
    billboard: string | null,
    blueCircle: string | null,
    yellowCircle: string | null,
  ) => {
    // Remove existing entities if they exist
    if (alternateSafeLocationBillboard.value)
      removeEntityIfExists(alternateSafeLocationBillboard.value)
    if (alternateBlueCircleEntity.value) removeEntityIfExists(alternateBlueCircleEntity.value)
    if (alternateYellowCircleEntity.value) removeEntityIfExists(alternateYellowCircleEntity.value)

    // Set new entities
    alternateSafeLocationBillboard.value = billboard
    alternateBlueCircleEntity.value = blueCircle
    alternateYellowCircleEntity.value = yellowCircle
  }

  // Helper to ensure we only have one safe location
  const setSafeLocation = (
    billboard: string | null,
    blueCircle: string | null,
    yellowCircle?: string | null,
  ) => {
    // Remove existing entities if they exist
    if (safeLocationBillboard.value) removeEntityIfExists(safeLocationBillboard.value)
    if (blueCircleEntity.value) removeEntityIfExists(blueCircleEntity.value)

    // Set new entities
    safeLocationBillboard.value = billboard
    blueCircleEntity.value = blueCircle
    if (yellowCircle) {
      removeEntityIfExists(yellowCircleEntity.value)
      yellowCircleEntity.value = yellowCircle
    }
  }

  ensurePersistedVisibilityWatcher()

  return {
    outerGeofenceEntity,
    innerGeofenceEntity,
    safeLocationBillboard,
    blueCircleEntity,
    yellowCircleEntity,
    alternateSafeLocationBillboard,
    alternateBlueCircleEntity,
    alternateYellowCircleEntity,
    homeMarkerBillboard,
    homeBlueCircleEntity,
    musterPointEntities,
    connectingLine,
    droneEntity,
    assetsStore,
    existingMusterPointIDs,
    goToEntity,
    toggleEntityVisibility,
    applyPersistedVisibility,
    // New helper functions
    addMusterPointEntity,
    clearAllMusterPoints,
    setSafeLocation,
    setAlternateSafeLocation,
    altitudeLine,
    trailingPathEntity,
    gtlMarkerEntities,
    takeoffHeightElevationEntity,
    takeoffHeightElevationLabelEntity,
    portalEntities,
    musterLabels,
    // Lifecycle / observability
    currentGroupId,
    $reset,
    beginPlot,
    endPlot,
    forceResetPlot,
    isPlotStuck,
    getCachedTerrain,
    setCachedTerrain,
    clearTerrainCache,
  }
}
